/**
 * Redux slice for gig shift requests.
 *
 * Wraps:
 *   GET /api/gig/requests?siteCd=XX&userId=XX&statusFilter=ALL
 *   PUT /api/gig/update
 *
 * The GET returns { details: GigRequestDetail[] } — one row per
 * Gig_Request_Header + Gig_Request_Detail join. Consumers can group by
 * `requestId` if they want a header-level view.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH, API_UPDATE, API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import {
    GigFilter,
    GigRequestDetail,
    GigRequestResponse,
    GigRequestStatusFilter,
    GigShiftCancelRequest,
    GigShiftCancelResponse,
    GigShiftUpdateRequest,
    GigShiftUpdateResponse,
    GigValidationErrorResponse,
} from '../../types/gigApi';

interface GigRequestsState {
    details: GigRequestDetail[];
    loading: boolean;
    error: string | null;
    currentRequestId: string | null;
    updating: boolean;
    updateError: string | null;
    lastUpdateResponse: GigShiftUpdateResponse | null;
    cancelling: boolean;
    cancelError: string | null;
    lastCancelResponse: GigShiftCancelResponse | null;
}

const initialState: GigRequestsState = {
    details: [],
    loading: false,
    error: null,
    currentRequestId: null,
    updating: false,
    updateError: null,
    lastUpdateResponse: null,
    cancelling: false,
    cancelError: null,
    lastCancelResponse: null,
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch gig shift requests for a site, optionally filtered by user id / status / date range.
 * Uses: GET /api/gig/requests?siteCd=XX&userId=XX&statusFilter=ALL&filter=all&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 */
export const fetchGigRequests = createAsyncThunk(
    'gigRequests/fetch',
    async (
        {
            siteCd,
            userId,
            statusFilter = 'ALL',
            filter = 'all',
            isSupervisorId = false,
            startDate,
            endDate,
        }: { siteCd: string; userId?: string; statusFilter?: GigRequestStatusFilter; filter?: GigFilter; isSupervisorId?: boolean; startDate?: string; endDate?: string },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams({ siteCd, statusFilter, filter });
            if (userId) params.append('userId', userId);
            if (isSupervisorId) params.append('isSupervisorId', 'true');
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            const response = await API_FETCH(`${ENDPOINTS.GIG_GET_REQUESTS}?${params.toString()}`);
            const envelope = response as GigRequestResponse | GigRequestDetail[];
            const details = Array.isArray(envelope) ? envelope : envelope?.details ?? [];
            return details;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch gig requests');
        }
    }
);

/**
 * Update one or more gig shift detail rows under a single Gig_Request_Id.
 * Uses: PUT /api/gig/update
 */
export const updateGigShift = createAsyncThunk<
    GigShiftUpdateResponse,
    GigShiftUpdateRequest,
    { rejectValue: { message: string; errors: string[] } }
>(
    'gigRequests/update',
    async (payload, { rejectWithValue }) => {
        try {
            const primaryDetail = payload.details?.[0];
            const requestBody: GigShiftUpdateRequest = {
                ...payload,
                requestSeqId: payload.requestSeqId ?? primaryDetail?.requestSeqId,
                requestDt: payload.requestDt ?? primaryDetail?.requestDt ?? null,
                startTime: payload.startTime ?? primaryDetail?.startTime ?? null,
                endTime: payload.endTime ?? primaryDetail?.endTime ?? null,
            };
            const response = await API_UPDATE(ENDPOINTS.GIG_UPDATE, requestBody);
            return response as GigShiftUpdateResponse;
        } catch (error: any) {
            const data = error?.data ?? error?.response?.data;
            const validation = data as GigValidationErrorResponse | undefined;
            return rejectWithValue({
                message: validation?.message || error?.message || 'Failed to update gig shift',
                errors: validation?.errors ?? [],
            });
        }
    }
);

/**
 * Cancel a single gig shift detail row (one day) under a Gig_Request_Id,
 * while it is still Pending.
 * Uses: POST /api/gig/cancel
 */
export const cancelGigShift = createAsyncThunk<
    GigShiftCancelResponse,
    GigShiftCancelRequest,
    { rejectValue: { message: string; errors: string[] } }
>(
    'gigRequests/cancel',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.GIG_CANCEL, payload);
            return response as GigShiftCancelResponse;
        } catch (error: any) {
            const data = error?.data ?? error?.response?.data;
            const validation = data as GigValidationErrorResponse | undefined;
            return rejectWithValue({
                message: validation?.message || error?.message || 'Failed to cancel gig shift',
                errors: validation?.errors ?? [],
            });
        }
    }
);

// ========================================
// SLICE
// ========================================
const gigRequestsSlice = createSlice({
    name: 'gigRequests',
    initialState,
    reducers: {
        clearGigRequests: (state) => {
            state.details = [];
            state.error = null;
        },
        clearGigUpdateError: (state) => {
            state.updateError = null;
        },
        clearGigCancelError: (state) => {
            state.cancelError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGigRequests.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(fetchGigRequests.fulfilled, (state, action) => {
                // Ignore stale responses from requests that have since been
                // superseded by a newer month/view navigation.
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.details = action.payload;
            })
            .addCase(fetchGigRequests.rejected, (state, action) => {
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch gig requests';
            })
            .addCase(updateGigShift.pending, (state) => {
                state.updating = true;
                state.updateError = null;
            })
            .addCase(updateGigShift.fulfilled, (state, action) => {
                state.updating = false;
                state.lastUpdateResponse = action.payload;
            })
            .addCase(updateGigShift.rejected, (state, action) => {
                state.updating = false;
                state.updateError = action.payload?.message ?? action.error?.message ?? 'Failed to update gig shift';
            })
            .addCase(cancelGigShift.pending, (state) => {
                state.cancelling = true;
                state.cancelError = null;
            })
            .addCase(cancelGigShift.fulfilled, (state, action) => {
                state.cancelling = false;
                state.lastCancelResponse = action.payload;
            })
            .addCase(cancelGigShift.rejected, (state, action) => {
                state.cancelling = false;
                state.cancelError = action.payload?.message ?? action.error?.message ?? 'Failed to cancel gig shift';
            });
    },
});

export const { clearGigRequests, clearGigUpdateError, clearGigCancelError } = gigRequestsSlice.actions;
export default gigRequestsSlice.reducer;
